import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformPanel } from '../../../components/AgentPlatform/AgentPlatformPanel';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { Button } from '../../../components/UI/Button';
import { useToast } from '../../../components/UI/ToastProvider';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type {
  AgentDto,
  AgentExecutionMonitoringSummary,
  AgentExecutionTimeSeriesPoint,
  AgentPlatformMonitoringSettings,
} from '../../../types/agentPlatform';
import { apTheme } from '../../../utils/agentPlatformTheme';

export const AgentPlatformMonitoringPage: React.FC = () => {
  const { addToast } = useToast();
  const [settings, setSettings] = useState<AgentPlatformMonitoringSettings | null>(null);
  const [summary, setSummary] = useState<AgentExecutionMonitoringSummary | null>(null);
  const [series, setSeries] = useState<AgentExecutionTimeSeriesPoint[]>([]);
  const [agents, setAgents] = useState<AgentDto[]>([]);
  const [agentId, setAgentId] = useState('');
  const [testOnly, setTestOnly] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = new Date();
      from.setDate(from.getDate() - 13);
      const [mon, sum, ts, agentList] = await Promise.all([
        agentPlatformService.getMonitoringSettings(),
        agentPlatformService.getExecutionSummary({
          agentId: agentId || undefined,
          isTestExecution: testOnly,
        }),
        agentPlatformService.getExecutionTimeSeries({
          agentId: agentId || undefined,
          isTestExecution: testOnly,
          from: from.toISOString(),
        }),
        agentPlatformService.getAgents(),
      ]);
      setSettings(mon);
      setSummary(sum);
      setSeries(ts);
      setAgents(agentList);
    } catch {
      addToast('Erro ao carregar monitoria.', 'error');
    } finally {
      setLoading(false);
    }
  }, [agentId, testOnly, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const maxExecutions = useMemo(
    () => Math.max(1, ...series.map(p => p.totalExecutions)),
    [series]
  );

  const toggleGlobal = async (enabled: boolean) => {
    try {
      const updated = await agentPlatformService.updateGlobalMonitoring({ enabled });
      setSettings(updated);
      addToast('Monitoria global atualizada.', 'success');
    } catch {
      addToast('Sem permissão para alterar monitoria global.', 'error');
    }
  };

  const toggleTenant = async (enabled: boolean) => {
    try {
      const updated = await agentPlatformService.updateTenantMonitoring({ enabled });
      setSettings(updated);
      addToast('Override do tenant atualizado.', 'success');
    } catch {
      addToast('Erro ao atualizar override do tenant.', 'error');
    }
  };

  return (
    <AgentPlatformFeatureGate feature="platform">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
          <AgentPlatformPageHeader
            title="Monitoria"
            subtitle="Dashboard operacional, tokens e políticas de coleta de execuções."
            area="operations"
            actions={
              <Link to="/agent-platform/governance">
                <Button variant="secondary">Governança</Button>
              </Link>
            }
          />

          {loading && (
            <p className="text-slate-500 animate-pulse py-8 text-center">Carregando monitoria…</p>
          )}

          {settings && (
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className={`${apTheme.governancePolicy} lg:col-span-1`}>
                <h2 className="text-base font-semibold text-slate-900 mb-1">Políticas</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Efetivo:{' '}
                  <span
                    className={
                      settings.effectiveEnabled ? 'text-emerald-600 font-semibold' : 'text-slate-400'
                    }
                  >
                    {settings.effectiveEnabled ? 'Coleta ativa' : 'Coleta inativa'}
                  </span>
                </p>
                <dl className="text-sm space-y-2 mb-4">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Global</dt>
                    <dd>{settings.globalEnabled ? 'On' : 'Off'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Override tenant</dt>
                    <dd>
                      {settings.tenantOverride == null
                        ? '—'
                        : settings.tenantOverride
                          ? 'Forçada ON'
                          : 'Forçada OFF'}
                    </dd>
                  </div>
                </dl>
                <p className="text-xs text-slate-500 mb-2">Global (operadora Baruk)</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button size="sm" onClick={() => toggleGlobal(true)}>
                    Global ON
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => toggleGlobal(false)}>
                    Global OFF
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => toggleTenant(true)}>
                    Tenant ON
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleTenant(false)}>
                    Tenant OFF
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        const updated = await agentPlatformService.updateTenantMonitoring({});
                        setSettings(updated);
                      } catch {
                        addToast('Erro ao limpar override.', 'error');
                      }
                    }}
                  >
                    Limpar override
                  </Button>
                </div>
              </div>

              <AgentPlatformPanel title="Filtros" className="lg:col-span-2">
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1 font-medium">Agente</label>
                    <select
                      className={apTheme.input}
                      value={agentId}
                      onChange={e => setAgentId(e.target.value)}
                    >
                      <option value="">Todos</option>
                      {agents.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={testOnly === undefined ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setTestOnly(undefined)}
                    >
                      Todas
                    </Button>
                    <Button
                      variant={testOnly === true ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setTestOnly(true)}
                    >
                      Testes
                    </Button>
                    <Button
                      variant={testOnly === false ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setTestOnly(false)}
                    >
                      Produção
                    </Button>
                  </div>
                </div>
              </AgentPlatformPanel>
            </div>
          )}

          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <StatCard label="Total" value={summary.totalExecutions} />
              <StatCard label="Concluídas" value={summary.completedCount} accent="emerald" />
              <StatCard label="Falhas" value={summary.failedCount} accent="red" />
              <StatCard label="Tokens" value={summary.totalTokens} accent="stone" />
            </div>
          )}

          <AgentPlatformPanel title="Execuções por dia (14 dias)" className="mb-8">
            {series.length === 0 ? (
              <p className="text-sm text-slate-500">Sem dados no período.</p>
            ) : (
              <div className="flex items-end gap-2 h-48 pt-4">
                {series.map(point => {
                  const height = `${Math.round((point.totalExecutions / maxExecutions) * 100)}%`;
                  const label = new Date(point.date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                  });
                  return (
                    <div
                      key={point.date}
                      className="flex-1 flex flex-col items-center gap-1 min-w-0"
                      title={`${point.totalExecutions} exec · ${point.failedCount} falhas`}
                    >
                      <div className="w-full flex flex-col justify-end h-36 gap-0.5">
                        <div
                          className="w-full rounded-t bg-red-500/80 transition-all"
                          style={{ height }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-500 truncate w-full text-center">
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </AgentPlatformPanel>

          {summary && (
            <div className="grid sm:grid-cols-2 gap-6">
              <AgentPlatformPanel title="Distribuição por status">
                <StatusBar
                  label="Concluídas"
                  value={summary.completedCount}
                  total={summary.totalExecutions}
                  color="bg-emerald-500"
                />
                <StatusBar
                  label="Falhas"
                  value={summary.failedCount}
                  total={summary.totalExecutions}
                  color="bg-red-500"
                />
                <StatusBar
                  label="Em execução"
                  value={summary.runningCount}
                  total={summary.totalExecutions}
                  color="bg-amber-400"
                />
                <StatusBar
                  label="Testes"
                  value={summary.testExecutionCount}
                  total={summary.totalExecutions}
                  color="bg-stone-500"
                />
              </AgentPlatformPanel>

              <AgentPlatformPanel title="Tokens">
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Prompt</dt>
                    <dd className="font-medium">{summary.totalPromptTokens.toLocaleString('pt-BR')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Completion</dt>
                    <dd className="font-medium">
                      {summary.totalCompletionTokens.toLocaleString('pt-BR')}
                    </dd>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <dt className="text-slate-700 font-medium">Total</dt>
                    <dd className="font-bold text-red-700">
                      {summary.totalTokens.toLocaleString('pt-BR')}
                    </dd>
                  </div>
                  {summary.averageDurationMs != null && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Duração média</dt>
                      <dd>{summary.averageDurationMs} ms</dd>
                    </div>
                  )}
                </dl>
              </AgentPlatformPanel>
            </div>
          )}
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'emerald' | 'red' | 'stone';
}) {
  const accentClass =
    accent === 'emerald'
      ? 'text-emerald-600'
      : accent === 'red'
        ? 'text-red-600'
        : accent === 'stone'
          ? 'text-stone-600'
          : 'text-slate-900';

  return (
    <div className={apTheme.statCard}>
      <p className="text-xs text-slate-500 uppercase font-semibold tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accentClass}`}>{value.toLocaleString('pt-BR')}</p>
    </div>
  );
}

function StatusBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-700">{label}</span>
        <span className="text-slate-500">
          {value} ({pct}%)
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
